import { useEffect, useMemo, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import { fetchPage } from '../api/pages';
import type { Page } from '../api/types';

type LoadState = 'loading' | 'found' | 'not-found';

export function StaticPage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<Page | null>(null);
  const [state, setState] = useState<LoadState>('loading');

  useEffect(() => {
    if (!slug) {
      setState('not-found');
      return;
    }
    setState('loading');
    fetchPage(slug)
      .then((p) => {
        setPage(p);
        setState('found');
      })
      .catch(() => setState('not-found'));
  }, [slug]);

  const safeHtml = useMemo(() => {
    if (!page) return '';
    return DOMPurify.sanitize(page.html, { USE_PROFILES: { html: true } });
  }, [page]);

  if (state === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (state === 'not-found' || !page) {
    return <Navigate to="/" replace />;
  }

  const seo = page.seo;

  return (
    <>
      <Helmet>
        <title>{seo.title ?? page.title} — Stututu</title>
        {seo.description && <meta name="description" content={seo.description} />}
        {seo.author && <meta name="author" content={seo.author} />}
        {seo.robots && <meta name="robots" content={seo.robots} />}
        {seo.canonical_url && <link rel="canonical" href={seo.canonical_url} />}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={seo.title ?? page.title} />
        {seo.description && <meta property="og:description" content={seo.description} />}
        {seo.image && <meta property="og:image" content={seo.image} />}
        <meta name="twitter:card" content={seo.image ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={seo.title ?? page.title} />
        {seo.description && <meta name="twitter:description" content={seo.description} />}
        {seo.image && <meta name="twitter:image" content={seo.image} />}
      </Helmet>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Box
          sx={{
            '& h1': { fontSize: { xs: '2rem', md: '2.5rem' }, mb: 2 },
            '& h2': { mt: 4, mb: 1.5 },
            '& p': { my: 1.5, lineHeight: 1.7 },
            '& ul, & ol': { my: 1.5, pl: 3 },
            '& a': { color: 'primary.main' },
            '& .block-hero': {
              textAlign: 'center',
              py: { xs: 4, md: 6 },
              '& .lead': { color: 'text.secondary', fontSize: '1.15rem' },
              '& .cta': {
                display: 'inline-block',
                mt: 2,
                px: 3,
                py: 1.25,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                borderRadius: 1,
                textDecoration: 'none',
              },
            },
            '& .block-rich-text': { py: 2 },
          }}
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
      </Container>
    </>
  );
}
